// src/pages/shippment/tabs/GenericFormTab.jsx
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setLoadTable } from '../../../redux/action/skuTable.action';
import { toast } from '../../../components/AppToast';
import { getSearchBySkuPO } from '../../../services/asnAPI/asn';
import { formatToDisplayDate, parseDisplayDate } from '../../../utils/dateFormatter';
import FacilityAutoSearch from "../../../components/FacilityAutoSearch";
import LookupField from "../../../components/LookupField";
import { SUPPLIER_LOOKUP, CUSTOMER_LOOKUP } from "../../../components/lookupConfigs";



// 🔑 DEDUPE HELPERS
const normalize = (v) =>
    String(v ?? "")
        .trim()
        .toLowerCase();

const buildDedupeKey = ({ sku, po, so }) => {
    if (!sku) return "";
    return [
        normalize(sku),
        normalize(po || "NA"),
        normalize(so || "NA"),
    ].join("::");
};


export default function GenericFormTab({
    fields = [],
    formData = {},
    onChange = () => { },
    errors = {},
    readOnly = false,
    isEdit = false
}) {
    const dispatch = useDispatch();
    const loadTable = useSelector(state => state.skutable?.TableDetails || {});
    const userInteractingRef = useRef(false);
    const viewOnly = !!(formData && formData.__viewOnly) || !!readOnly;

    // Ensure receiptStatus default (existing)
    useEffect(() => {
        if ((formData.receiptStatus == null || formData.receiptStatus === '') && typeof onChange === 'function') {
            onChange({ target: { name: 'receiptStatus', value: 'New' } });
        }
    }, [formData.receiptStatus, onChange]);

    // --- NEW: set default values from field definitions (defaultValue / prefetch) if missing ---
    useEffect(() => {
        if (!fields || typeof onChange !== 'function') return;
        fields.forEach(f => {
            if (!f || !f.name) return;
            const exists = formData[f.name] !== undefined && formData[f.name] !== null && formData[f.name] !== '';
            if (!exists) {
                if (f.defaultValue !== undefined && f.defaultValue !== null) {
                    onChange({ target: { name: f.name, value: f.defaultValue } });
                } else if (f.prefetch && f.defaultValue !== undefined) {
                    // If you want to support prefetch behaviour, expand here.
                    onChange({ target: { name: f.name, value: f.defaultValue } });
                }
            }
        });
        // only run on mount / when fields change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fields]);

    // --- helpers to normalize display/storage values when formData might contain objects/arrays ---
    const extractPrimitiveValue = (val) => {
        if (val === undefined || val === null) return "";
        if (Array.isArray(val)) {
            // if array of objects with label/value, map to label or value; else join primitives
            const mapped = val.map(v => {
                if (v && typeof v === 'object') return v.label ?? v.value ?? JSON.stringify(v);
                return String(v);
            });
            return mapped.join(', ');
        }
        if (typeof val === 'object') {
            // object like { value, label } or others
            return val.label ?? val.value ?? JSON.stringify(val);
        }
        return String(val);
    };

    // get the raw value for select's value attribute (must be primitive)
    const selectValueForField = (fieldName) => {
        const v = formData[fieldName];
        if (v === undefined || v === null) return "";
        if (typeof v === 'object') {
            // if object has value property, return that; else use label; else stringify
            return v.value ?? v.label ?? extractPrimitiveValue(v);
        }
        return v;
    };

    // --- handleSearch (search SKU / PO / Orders and populate loadTable) ---
    // --- handleSearch (search SKU / PO / Orders and populate loadTable) ---
   



    const handleSearchOLD = async (fieldName) => {
        if (!fieldName) return;
        if (viewOnly) return;

        userInteractingRef.current = true;

        // ----- field -> type mapping -----
        const fieldToTypeMap = {
            skuSearch: 4,
            SKUID: 4,
            poNumber: 1,
            orderNumbers: 3, // server may treat 3/4 as SO/ORDER; normalize below
        };

        const type = fieldToTypeMap[fieldName];
        if (!type) {
            userInteractingRef.current = false;
            return;
        }

        // ----- respect createUsing in create mode (header guard) -----
        if (!isEdit && (formData.createUsing !== undefined && formData.createUsing !== null)) {
            const createUsingVal = selectValueForField('createUsing'); // primitive
            if ((createUsingVal === '1' && type !== 1) || (createUsingVal === '2' && type !== 2)) {
                userInteractingRef.current = false;
                return;
            }
        }

        // ----- derive searchValue (more robust) -----
        let searchValue = "";
        if (typeof formData[fieldName] === "string" && formData[fieldName].trim() !== "") {
            searchValue = formData[fieldName].trim();
        } else {
            if (type === 2) {
                searchValue = (formData.SKUID || formData.skuSearch || "").toString().trim();
            } else if (type === 1) {
                searchValue = (formData.poNumber || "").toString().trim();
            } else if (type === 3 || type === 4) {
                searchValue = (formData.orderNumbers || "").toString().trim();
            }
        }
        if (!searchValue) {
            userInteractingRef.current = false;
            return;
        }

        // stable UI method derived from fieldName (used elsewhere)
        const method =
            (fieldName === 'SKUID' || fieldName === 'skuSearch') ? 'sku' :
                (fieldName === 'poNumber') ? 'po' :
                    'so'; // orderNumbers => 'so'

        try {
            const apiResp = await getSearchBySkuPO(type, [searchValue]);

            // accept either { data: [...] } or bare array
            const blocks = Array.isArray(apiResp?.data)
                ? apiResp.data
                : Array.isArray(apiResp)
                    ? apiResp
                    : [];

            if (!blocks.length) {
                toast.error(`No results found for ${(fieldName === 'SKUID' || fieldName === 'skuSearch')
                    ? 'SKU ID'
                    : (fieldName === 'poNumber' ? 'PO Number' : 'Order Number')
                    }: ${searchValue}`);
                userInteractingRef.current = false;
                return;
            }

            // ==== Build de-dupe maps (per-group keys chosen to allow required rows) ====
            // existingSKUsGlobal kept for any legacy global dedupe needs (not used for SO now)
            const existingSKUsGlobal = new Set();
            // existingSKUsPerGroup stores Sets keyed by groupKey (PO::xxx, SKU::xxx, SO::xxx)
            // For PO groups we store sku::po; for SKU groups store sku::po if present else sku;
            // for SO groups store sku::salesOrder (so multiple sales orders show up)
            const existingSKUsPerGroup = {};

            Object.entries(loadTable || {}).forEach(([k, items]) => {
                if (k.startsWith('__')) return;
                if (!Array.isArray(items)) return;
                const perSet = new Set();
                items.forEach(it => {
                    const sku = (it?.skuId || it?.item || '').toString().toLowerCase().trim();
                    if (!sku) return;

                    existingSKUsGlobal.add(sku);

                    // derive PO / SO key candidates from existing item
                    const po = (it?.purchaseOrder || it?.poNumber || '').toString().toLowerCase().trim();
                    const so = (it?.orderNumber || it?.soNumber || it?.salesOrder || '').toString().toLowerCase().trim();

                    if (k.startsWith('PO::')) {
                        perSet.add(`${sku}::${po}`);
                    } else if (k.startsWith('SO::') || k.startsWith('ORDER::')) {
                        // store sku::so so different sales orders are distinct
                        perSet.add(so ? `${sku}::${so}` : sku);
                    } else {
                        // SKU:: or other groups
                        perSet.add(po ? `${sku}::${po}` : sku);
                    }
                });
                if (k.startsWith('PO::') || k.startsWith('SKU::') || k.startsWith('ORDER::') || k.startsWith('SO::')) {
                    existingSKUsPerGroup[k] = perSet;
                }
            });

            const updated = { ...(loadTable || {}) };
            const pad4 = (n) => String(n).padStart(4, '0');

            let totalAdded = 0;
            let duplicateCount = 0;
            let lastGroupKey = null;

            for (const block of blocks) {
                const items = Array.isArray(block?.items) ? block.items : [];
                if (!items.length) continue;

                // Prefer server hint for grouping; else infer from current search "type"
                // Convention: 1=PO, 2=SKU, 3/4=ORDER/SO
                const createUsingFromServer = Number(block?.createUsing);

                // Treat createUsing === 3 as PO-style (if server sends 3 for PO-like)
                const isPO = createUsingFromServer
                    ? (createUsingFromServer === 1 || createUsingFromServer === 3)
                    : (type === 1);
                const isSKU = createUsingFromServer ? (createUsingFromServer === 2) : (type === 2);
                const isSO = !isPO && !isSKU; // orders

                const rawKey = (block?.groupKey || '').toString().trim() || searchValue;
                const groupPrefix = isPO ? 'PO' : isSKU ? 'SKU' : 'SO';
                const groupKey = `${groupPrefix}::${rawKey}`;

                const existingArr = Array.isArray(updated[groupKey]) ? [...updated[groupKey]] : [];
                const enrichedToAdd = [];

                // choose dedupe strategy:
                // - PO: per-group sku::po
                // - SKU: per-group sku::po if po present else sku
                // - SO: per-group sku::so (use salesOrder/soNumber/orderNumber)
                const perGroupSet = existingSKUsPerGroup[groupKey] ? new Set(existingSKUsPerGroup[groupKey]) : new Set();

                items.forEach((r, idx) => {
                    const skuRaw = (r?.skuId || r?.item || '').toString().trim();
                    const skuKey = skuRaw.toLowerCase();
                    if (!skuKey) return;

                    const poForKey = (r.purchaseOrder || r.poNumber || '').toString().trim().toLowerCase();
                    const soForKey = (r.orderNumber || r.soNumber || r.salesOrder || rawKey || '').toString().trim().toLowerCase();

                    // build dedupeKey according to group type
                    let dedupeKey;
                    if (isPO) {
                        dedupeKey = poForKey ? `${skuKey}::${poForKey}` : skuKey;
                    } else if (isSKU) {
                        dedupeKey = poForKey ? `${skuKey}::${poForKey}` : skuKey;
                    } else { // isSO
                        dedupeKey = soForKey ? `${skuKey}::${soForKey}` : skuKey;
                    }

                    const isDup = dedupeKey && perGroupSet.has(dedupeKey);

                    if (isDup) {
                        duplicateCount++;
                        return;
                    }

                    const nextLine = r.lineNumber || (existingArr.length + enrichedToAdd.length + 1);

                    enrichedToAdd.push({
                        ...r,

                        lineNo: pad4(nextLine),
                        item: r.skuId || r.item || '',
                        skuId: r.skuId || r.item || '',

                        purchaseOrder: r.purchaseOrder || r.poNumber || (isPO ? rawKey : r.purchaseOrder),
                        poNumber: r.purchaseOrder || r.poNumber || (isPO ? rawKey : r.poNumber),

                        orderNumber: r.orderNumber || r.soNumber || r.salesOrder || (!isPO && !isSKU ? rawKey : r.orderNumber),

                        description: r.description || `Item ${r.skuId || r.item || rawKey}`,
                        pack: r.pack || 'STD',
                        uom: r.uom || 'EA',

                        expectedQty: r.orderedQty ?? r.expectedQty ?? 0,
                        dispatchQty: r.dispatchQty ?? 0,
                        shortQty: r.shortQty ?? 0,
                        excessQty: r.excessQty ?? 0,
                        damagedQty: r.damagedQty ?? 0,
                        openQty: r.openQty ?? r.openQuantity ?? (r.orderedQty ?? 0),

                        unitPrice: r.unitPrice ?? r.poUnitPrice ?? 0,
                        discount: r.discount ?? r.poDiscount ?? 0,
                        tax: r.tax ?? 0,

                        asnUnitPrice: (r.asnUnitPrice ?? r.unitPrice ?? r.poUnitPrice ?? '').toString(),
                        asnDiscount: (r.asnDiscount ?? r.discount ?? r.poDiscount ?? '').toString(),

                        // header hint like ASN version (2=PO, 1=SKU, 3=SO)
                        _createUsing: isPO ? 2 : isSKU ? 1 : 3,
                    });

                    // update per-group set
                    if (dedupeKey) {
                        perGroupSet.add(dedupeKey);
                    }
                });

                // if (enrichedToAdd.length) {
                //     updated[groupKey] = [...existingArr, ...enrichedToAdd];
                //     existingSKUsPerGroup[groupKey] = perGroupSet;
                //     totalAdded += enrichedToAdd.length;
                //     lastGroupKey = groupKey;
                // }

                if (enrichedToAdd.length > 0) {
                    // ✅ only append when something new exists
                    updated[groupKey] = existingArr.concat(enrichedToAdd);
                    existingSKUsPerGroup[groupKey] = perGroupSet;
                    totalAdded += enrichedToAdd.length;
                    lastGroupKey = groupKey;
                }

            }

            if (totalAdded === 0) {
                if (duplicateCount > 0) {
                    toast.warning(`${duplicateCount} duplicate SKU(s) skipped. No new items added.`);
                } else {
                    toast.info('No new items to add.');
                }
                userInteractingRef.current = false;
                return;
            }

            // carry over the UI meta you use elsewhere
            updated.__lastAddedTable = lastGroupKey;
            updated.__userTriggered = true;
            updated.__origin = {
                method,          // 'po' | 'sku' | 'so' (derived from input field)
                value: searchValue,
                groupKey: lastGroupKey
            };
            updated.__createUsing = method;
            

            dispatch(setLoadTable(updated));

            if (duplicateCount > 0) {
                toast.info(`${duplicateCount} duplicate SKU(s) skipped. Added ${totalAdded} new item(s).`);
            } else {
                toast.success(`Added ${totalAdded} item(s).`);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            toast.error(`Failed to fetch data: ${error?.message || "Unknown error"}`);
        } finally {
            // small delay to avoid flapping UI if user clicks rapidly
            setTimeout(() => {
                userInteractingRef.current = false;
            }, 600);
        }
    };

    const handleSearch = async (fieldName) => {
        if (!fieldName || viewOnly) return;

        userInteractingRef.current = true;

        const fieldToTypeMap = {
            skuSearch: 4,
            SKUID: 4,
            poNumber: 1,
            orderNumbers: 3,
        };

        const type = fieldToTypeMap[fieldName];
        if (!type) return;

        // ----- derive searchValue -----
        const searchValue =
            typeof formData[fieldName] === "string"
                ? formData[fieldName].trim()
                : "";

        if (!searchValue) return;

        try {
            const apiResp = await getSearchBySkuPO(type, [searchValue]);

            const blocks = Array.isArray(apiResp?.data)
                ? apiResp.data
                : Array.isArray(apiResp)
                    ? apiResp
                    : [];

            if (!blocks.length) {
                toast.warning("No results found");
                return;
            }

            // 🔐 GLOBAL DEDUPE SET (single source of truth)
            const globalDedupeSet = new Set();

            Object.values(loadTable || {}).forEach((rows) => {
                if (!Array.isArray(rows)) return;

                rows.forEach((r) => {
                    const key = buildDedupeKey({
                        sku: r.skuId || r.item,
                        po: r.purchaseOrder || r.poNumber,
                        so: r.orderNumber || r.soNumber || r.salesOrder,
                    });
                    if (key) globalDedupeSet.add(key);
                });
            });

            const updated = { ...(loadTable || {}) };
            const pad4 = (n) => String(n).padStart(4, "0");

            let totalAdded = 0;
            let duplicateCount = 0;
            let lastGroupKey = null;

            for (const block of blocks) {
                const items = Array.isArray(block?.items) ? block.items : [];
                if (!items.length) continue;

                const rawKey = normalize(block?.groupKey || searchValue);
                const groupPrefix =
                    type === 1 ? "PO" : type === 4 ? "SKU" : "SO";
                const groupKey = `${groupPrefix}::${rawKey}`;

                const existingArr = Array.isArray(updated[groupKey])
                    ? [...updated[groupKey]]
                    : [];

                const toAdd = [];

                items.forEach((r) => {
                    const dedupeKey = buildDedupeKey({
                        sku: r.skuId || r.item,
                        po: r.purchaseOrder || r.poNumber,
                        so: r.orderNumber || r.soNumber || r.salesOrder,
                    });

                    // 🚫 DUPLICATE BLOCK
                    if (!dedupeKey || globalDedupeSet.has(dedupeKey)) {
                        duplicateCount++;
                        return;
                    }

                    globalDedupeSet.add(dedupeKey);

                    const nextLine =
                        r.lineNumber ||
                        existingArr.length + toAdd.length + 1;

                    toAdd.push({
                        ...r,
                        lineNo: pad4(nextLine),

                        item: r.skuId || r.item || "",
                        skuId: r.skuId || r.item || "",

                        purchaseOrder: r.purchaseOrder || r.poNumber || "",
                        poNumber: r.purchaseOrder || r.poNumber || "",

                        orderNumber:
                            r.orderNumber ||
                            r.soNumber ||
                            r.salesOrder ||
                            "",

                        description:
                            r.description ||
                            `Item ${r.skuId || r.item || rawKey}`,

                        pack: r.pack || "STD",
                        uom: r.uom || "EA",

                        expectedQty:
                            r.orderedQty ?? r.expectedQty ?? 0,
                        dispatchQty: r.dispatchQty ?? 0,
                        shortQty: r.shortQty ?? 0,
                        excessQty: r.excessQty ?? 0,
                        damagedQty: r.damagedQty ?? 0,
                        openQty:
                            r.openQty ??
                            r.openQuantity ??
                            (r.orderedQty ?? 0),

                        unitPrice: r.unitPrice ?? r.poUnitPrice ?? 0,
                        discount: r.discount ?? r.poDiscount ?? 0,
                        tax: r.tax ?? 0,

                        asnUnitPrice: String(
                            r.asnUnitPrice ??
                            r.unitPrice ??
                            r.poUnitPrice ??
                            ""
                        ),
                        asnDiscount: String(
                            r.asnDiscount ??
                            r.discount ??
                            r.poDiscount ??
                            ""
                        ),
                    });
                });

                if (toAdd.length > 0) {
                    updated[groupKey] = existingArr.concat(toAdd);
                    totalAdded += toAdd.length;
                    lastGroupKey = groupKey;
                }
            }

            // ⛔ STOP redux update if nothing new
            if (totalAdded === 0) {
                toast.warning(
                    duplicateCount > 0
                        ? "Duplicate data. No new rows added."
                        : "No new items to add."
                );
                return;
            }

            updated.__lastAddedTable = lastGroupKey;
            updated.__userTriggered = true;

            dispatch(setLoadTable(updated));
            toast.success(`Added ${totalAdded} item(s).`);
        } catch (error) {
            console.error("Error fetching data:", error);
            toast.error("Failed to fetch data");
        } finally {
            setTimeout(() => {
                userInteractingRef.current = false;
            }, 600);
        }
    };

    

    // Handle date / input conversions
    const handleInputChange = (e) => {
        const { name, value, type } = e.target || {};
        if (!name) return;

        // date input (yyyy-mm-dd) => ISO
        if (type === 'date' && value) {
            try {
                const [y, m, d] = value.split('-').map(v => Number(v));
                if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
                    const dt = new Date(y, m - 1, d);
                    onChange({ target: { name, value: dt.toISOString() } });
                    return;
                }
            } catch (err) { /* fallthrough */ }
        }

        // typed display date (dd/mm/yyyy or dd-mm-yyyy)
        if (type === 'text' && value && (value.includes('/') || value.includes('-'))) {
            try {
                const isoDate = parseDisplayDate(value);
                if (isoDate) {
                    onChange({ target: { name, value: isoDate } });
                    return;
                }
            } catch (err) { /* ignore */ }
        }

        onChange(e);
    };

    const datePickerFields = [
        'expectedDepartureDate',
        'actualDepartureDate',
        'expectedArrivalDate',
        'actualArrivalDate'
    ];

    const patch = (map) =>
        Object.entries(map).forEach(([name, value]) => onChange({ target: { name, value } }));

    /** Supplier picked from `supplier/fetch-multi-dynamic` → Source / Seller section. */
    const applySupplier = (fieldName, rec) => {
        const s = SUPPLIER_LOOKUP.normalize(rec);
        patch({
            [fieldName]: s.name,
            sourceAddressLine1: s.address1,
            sourceAddressLine2: s.address2,
            sourceAddressLine3: "",
            sourceAddressLine4: "",
            sourceCity: s.city,
            sourceState: s.state,
            sourceCountry: s.country,
            sourceReference: s.id,
            sourceContact: s.mobile,
            sourceEmail: s.email,
        });
    };

    /** Customer picked from `customer-group/fetch-multi-dynamic` → Destination / Buyer section. */
    const applyCustomer = (fieldName, rec) => {
        const c = CUSTOMER_LOOKUP.normalize(rec);
        patch({
            [fieldName]: c.name,
            destinationAddressLine1: c.address1,
            destinationAddressLine2: c.address2,
            destinationAddressLine3: "",
            destinationAddressLine4: "",
            destinationCity: c.city,
            destinationState: c.state,
            destinationCountry: c.country,
            destinationReference: c.id,
            destinationContact: c.mobile,
            destinationEmail: c.email,
        });
    };

    const getDisplayValue = (field, value) => {
        if (field.type === 'date' && value) {
            return formatToDisplayDate(value);
        }
        return extractPrimitiveValue(value);
    };

    const visibleFields = (fields || []).filter(f => {
        // Respect conditional visibility first
        if (f.conditional) {
            const actual = selectValueForField(f.conditional.field) || extractPrimitiveValue(formData[f.conditional.field]);
            if (String(actual) !== String(f.conditional.value)) return false;
        }

        // If field is marked hiddenOnCreate, keep it hidden while creating (i.e. when there's no value yet).
        // This ensures fields like "shipmentNumber" (auto-generated) remain hidden on the create form
        // and only show when the record has a value (when you open an existing row).
        if (f.hiddenOnCreate) {
            const val = formData?.[f.name];
            const primitive = extractPrimitiveValue(val);
            if (primitive === "" || primitive === undefined || primitive === null) {
                return false;
            }
        }

        return true;
    });

    const hasTableData = Object.keys(loadTable || {}).length > 0;

    // file input renderer
    const renderFileInput = (field) => {
        const currentFiles = formData[field.name];
        const isInvalid = !!errors?.[field.name];
        return (
            <>
                <input
                    id={field.name}
                    type="file"
                    className={`form-control ${isInvalid ? 'is-invalid' : ''}`}
                    name={field.name}
                    onChange={(e) => { const files = e.target.files; onChange({ target: { name: field.name, value: files } }); }}
                    disabled={viewOnly || field.readOnly}
                />
                {isInvalid && <div className="invalid-feedback d-block">{errors[field.name] || 'Required'}</div>}
                {currentFiles && currentFiles.length > 0 ? (
                    <div className="form-text mt-1">
                        {Array.from(currentFiles).map((f, i) => <div key={i}>{f.name}</div>)}
                    </div>
                ) : null}
            </>
        );
    };

    return (
        <>
            <div className="row gx-2 mx-2">
                {visibleFields.map((field) => {
                    if (field.hiddenOnCreate && !isEdit && !viewOnly) return null;

                    const inputType = field.inputType || field.type;
                    const isInvalid = !!errors?.[field.name];

                    return (
                        <div className="col-md-2" key={field.name}>
                            <div className="form-group mb-3">
                                <label htmlFor={field.name} className="form-label">
                                    {field.label} {field.required && <span className="text-danger">*</span>}
                                </label>

                                {field.type === "select" ? (
                                    <>
                                        {(() => {
                                            const isCreateUsing = field.name === 'createUsing';
                                            const disabledForCreateUsing = viewOnly || hasTableData;
                                            const disabled = isCreateUsing ? disabledForCreateUsing : (field.readOnly || viewOnly);
                                            const currentVal = selectValueForField(field.name);

                                            return (
                                                <select
                                                    id={field.name}
                                                    className={`form-select ${isInvalid ? 'is-invalid' : ''}`}
                                                    name={field.name}
                                                    value={currentVal}
                                                    onChange={(e) => onChange({ target: { name: field.name, value: e.target.value } })}
                                                    required={field.required}
                                                    disabled={disabled}
                                                >
                                                    <option value="">Select</option>
                                                    {field.options?.map((opt) =>
                                                        typeof opt === 'object' ? (
                                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                        ) : (
                                                            <option key={opt} value={opt}>{opt}</option>
                                                        )
                                                    )}
                                                </select>
                                            );
                                        })()}
                                        {isInvalid && <div className="invalid-feedback d-block">{errors[field.name] || 'Required'}</div>}
                                    </>
                                ) : field.type === "multiselect" ? (
                                    <>
                                        <select
                                            id={field.name}
                                            className={`form-select ${isInvalid ? 'is-invalid' : ''}`}
                                            multiple
                                            name={field.name}
                                            value={
                                                Array.isArray(formData[field.name])
                                                    ? formData[field.name].map(v => (typeof v === 'object' ? (v.value ?? v.label ?? JSON.stringify(v)) : String(v)))
                                                    : []
                                            }
                                            onChange={(e) =>
                                                onChange({
                                                    target: {
                                                        name: field.name,
                                                        value: Array.from(e.target.selectedOptions, opt => opt.value)
                                                    }
                                                })
                                            }
                                            disabled={viewOnly || field.readOnly}
                                        >
                                            {field.options?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                        </select>
                                        {isInvalid && <div className="invalid-feedback d-block">{errors[field.name] || 'Required'}</div>}
                                    </>
                                ) : field.type === "supplierLookup" || field.type === "customerLookup" ? (
                                    <LookupField
                                        name={field.name}
                                        value={extractPrimitiveValue(formData[field.name])}
                                        config={field.type === "supplierLookup" ? SUPPLIER_LOOKUP : CUSTOMER_LOOKUP}
                                        editable
                                        disabled={viewOnly || field.readOnly}
                                        required={field.required}
                                        error={isInvalid ? (errors[field.name] || `${field.label} is required`) : ""}
                                        onChange={(e) => onChange(e)}
                                        onSelect={(rec) =>
                                            field.type === "supplierLookup"
                                                ? applySupplier(field.name, rec)
                                                : applyCustomer(field.name, rec)
                                        }
                                    />
                                ) : field.type === "autosearch" ? (
                                    <>
                                        <FacilityAutoSearch
                                            value={extractPrimitiveValue(formData[field.name]) ?? ""}
                                            required={field.required}
                                            error={isInvalid ? (errors[field.name] || `${field.label} is required`) : ""}
                                            disabled={viewOnly || field.readOnly}
                                            onSelect={(facility) => {
                                                if (!facility) return;

                                                // primary text value (selected facility name)
                                                onChange({
                                                    target: {
                                                        name: field.name,
                                                        value: facility?.Facility_Name || "",
                                                    },
                                                });

                                                const addr = facility?.Facility_Address || {};
                                                const contact = facility?.Facility_ManagerContact || {};

                                                const isBuyer =
                                                    field.name === "destinationName" || field.name === "buyer";
                                                const isSupplier =
                                                    field.name === "sourceName" || field.name === "supplierOwner";

                                                if (isBuyer) {
                                                    const buyerMap = {
                                                        destinationAddressLine1: addr.address1 || "",
                                                        destinationAddressLine2: addr.address2 || "",
                                                        destinationAddressLine3: addr.address3 || "",
                                                        destinationAddressLine4: addr.address4 || "",
                                                        destinationCity: addr.city || "",
                                                        destinationState: addr.state || "",
                                                        destinationCountry: addr.country || "",
                                                        destinationReference: facility.Facility_ID || "",
                                                        destinationContact: contact.mobile || "",
                                                        destinationEmail: contact.email || "",
                                                    };
                                                    Object.entries(buyerMap).forEach(([k, v]) =>
                                                        onChange({ target: { name: k, value: v } })
                                                    );
                                                }

                                                if (isSupplier) {
                                                    const supplierMap = {
                                                        sourceAddressLine1: addr.address1 || "",
                                                        sourceAddressLine2: addr.address2 || "",
                                                        sourceAddressLine3: addr.address3 || "",
                                                        sourceAddressLine4: addr.address4 || "",
                                                        sourceCity: addr.city || "",
                                                        sourceState: addr.state || "",
                                                        sourceCountry: addr.country || "",
                                                        sourceReference: facility.Facility_ID || "",
                                                        sourceContact: contact.mobile || "",
                                                        sourceEmail: contact.email || "",
                                                    };
                                                    Object.entries(supplierMap).forEach(([k, v]) =>
                                                        onChange({ target: { name: k, value: v } })
                                                    );
                                                }
                                            }}
                                        />
                                        {isInvalid && <div className="invalid-feedback d-block">{errors[field.name] || 'Required'}</div>}
                                    </>
                                ) : field.searchable ? (
                                    <div className="input-group">
                                        <input
                                            id={field.name}
                                            type={inputType}
                                            className={`form-control ${isInvalid ? 'is-invalid' : ''}`}
                                            name={field.name}
                                            value={extractPrimitiveValue(formData[field.name])}
                                            onChange={(e) => { onChange(e); }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleSearch(field.name);
                                                }
                                            }}
                                            onFocus={() => { userInteractingRef.current = true; }}
                                            onBlur={() => { setTimeout(() => { userInteractingRef.current = false; }, 500); }}
                                            required={field.required}
                                            disabled={viewOnly || field.readOnly}
                                        />
                                        <button
                                            className="btn btn-outline-secondary"
                                            type="button"
                                            onClick={() => handleSearch(field.name)}
                                            disabled={viewOnly}
                                        >
                                            <i className="bi bi-search"></i>
                                        </button>
                                        {isInvalid && <div className="invalid-feedback d-block">{errors[field.name] || 'Required'}</div>}
                                    </div>

                                ) : inputType === "file" ? (
                                    renderFileInput(field)

                                ) : field.type === "date" && datePickerFields.includes(field.name) ? (
                                    <>
                                        <input
                                            id={field.name}
                                            type={viewOnly ? "text" : "date"}
                                            className={`form-control ${isInvalid ? 'is-invalid' : ''}`}
                                            name={field.name}
                                            value={
                                                viewOnly
                                                    ? getDisplayValue(field, formData[field.name])
                                                    : (formData[field.name] ? (String(formData[field.name]).split('T')[0]) : "")
                                            }
                                            onChange={(e) => {
                                                const { name, value, type } = e.target;
                                                if (type === 'date' && value) {
                                                    const [y, m, d] = value.split('-').map(v => Number(v));
                                                    const dt = new Date(y, m - 1, d);
                                                    onChange({ target: { name, value: dt.toISOString() } });
                                                } else {
                                                    onChange(e);
                                                }
                                            }}
                                            required={field.required}
                                            disabled={viewOnly || field.readOnly}
                                            placeholder={viewOnly ? "" : "dd-mm-yyyy"}
                                        />
                                        {isInvalid && <div className="invalid-feedback d-block">{errors[field.name] || 'Required'}</div>}
                                    </>
                                ) : (
                                    <>
                                        <input
                                            id={field.name}
                                            type={field.type === 'date' ? 'text' : field.type}
                                            className={`form-control ${isInvalid ? 'is-invalid' : ''}`}
                                            name={field.name}
                                            value={getDisplayValue(field, formData[field.name])}
                                            onChange={(e) => {
                                                const { name, value, type } = e.target;
                                                if (type === 'text' && (value.includes('/') || value.includes('-'))) {
                                                    const iso = parseDisplayDate(value);
                                                    if (iso) { onChange({ target: { name, value: iso } }); return; }
                                                }
                                                onChange(e);
                                            }}
                                            required={field.required}
                                            disabled={viewOnly || field.readOnly}
                                            placeholder={field.type === 'date' ? 'dd-mm-yyyy' : ''}
                                        />
                                        {isInvalid && <div className="invalid-feedback d-block">{errors[field.name] || 'Required'}</div>}
                                    </>
                                )}

                            </div>
                        </div>
                    );
                })}
            </div>

        </>
    );
}
